import React, { forwardRef, useState, Fragment, useImperativeHandle, useRef } from 'react';
import { Box, type Types } from '@fiverr-private/theme';
import CustomControls from './components/CustomControls';
import Sources from './components/Sources';
import Subtitles from './components/Subtitles';
import type { VideoProps } from './types';

const Video = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      controls = true,
      sources = [],
      poster = '',
      preload = '',
      autoPlay = false,
      width = '100%',
      objectFit = 'cover',
      subtitles = [],
      playOnHover,
      withHoverActions = true,
      muted,
      onMouseEnter,
      onMouseLeave,
      onMouseOut,
      preventHoverPause,
      ...restProps
    },
    ref
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isMuted, setIsMuted] = useState(playOnHover ? true : muted);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isAssetsLoaded, setIsAssetsLoaded] = useState(false);
    const includeHoverActions = playOnHover && withHoverActions;

    useImperativeHandle<HTMLVideoElement | null, HTMLVideoElement | null>(ref, () => videoRef.current);

    const handleMouseEnter = (e: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
      if (playOnHover) {
        videoRef.current?.play();
        setIsPlaying(true);
        setIsAssetsLoaded(true);
      }

      onMouseEnter?.(e);
    };

    const handleMouseOut = (e: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
      if (playOnHover && isPlaying && !preventHoverPause?.(e)) {
        videoRef.current?.pause();
        setIsPlaying(false);
      }

      onMouseOut?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
      if (playOnHover && isPlaying && !preventHoverPause?.(e)) {
        videoRef.current?.pause();
        setIsPlaying(false);
      }
      onMouseLeave?.(e);
    };

    const Wrapper = includeHoverActions ? Box : Fragment;
    const wrapperProps = includeHoverActions
      ? ({
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        } as Types.BoxType)
      : {};

    return (
      <Wrapper {...wrapperProps}>
        {includeHoverActions && (
          <CustomControls
            videoRef={videoRef}
            isMuted={isMuted}
            setIsMuted={setIsMuted}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
          />
        )}
        <Box
          as="video"
          objectFit={objectFit}
          ref={videoRef}
          autoPlay={autoPlay}
          controls={playOnHover ? false : controls}
          muted={isMuted}
          poster={poster}
          preload={preload}
          crossOrigin="anonymous"
          role="video"
          width={width}
          onMouseEnter={includeHoverActions ? undefined : handleMouseEnter}
          onMouseLeave={includeHoverActions ? undefined : handleMouseLeave}
          onMouseOut={handleMouseOut}
          {...restProps}
        >
          {(!playOnHover || isAssetsLoaded) && <Sources sources={sources} />}
          <Subtitles subtitles={subtitles} />
        </Box>
      </Wrapper>
    );
  }
);

Video.displayName = 'Video';

export default Video;
